/**
 * In-memory store for tracked Word ranges.
 *
 * Tracked ranges survive across `Word.run()` batches, allowing us to mark
 * a document location and return to it later without modifying the document
 * structure (unlike content controls which insert XML).
 *
 * Ranges are stored with unique IDs for retrieval during setSnippet.
 *
 * Memory Management:
 * - LRU eviction when MAX_TRACKED_RANGES is exceeded
 * - Time-based expiration after RANGE_EXPIRY_MS
 * - Released range IDs are tracked for helpful error messages
 */

/**
 * Metadata stored for re-finding ranges in new contexts.
 */
interface RangeMetadata {
  /** The text content of the range (for searching) */
  text: string;
  /** Text immediately before the range (for disambiguation) */
  contextBefore?: string;
  /** Text immediately after the range (for disambiguation) */
  contextAfter?: string;
}

/**
 * Tracked range entry with timestamps for LRU eviction.
 */
interface TrackedRangeEntry {
  range: Word.Range;
  createdAt: number;
  lastAccessedAt: number;
  /**
   * If true, this range came from word_add_in_query_range and should persist
   * after edits. If false/undefined, the range is ephemeral and should be
   * removed after being used for setSnippet.
   */
  persistent?: boolean;
  /**
   * Metadata for re-finding the range in a new Word context.
   * Used when the original range is in a stale context.
   */
  metadata?: RangeMetadata;
}

/**
 * Maximum number of tracked ranges to keep in memory.
 * When exceeded, the least-recently-used ranges are evicted.
 */
const MAX_TRACKED_RANGES = 100;

/**
 * Time after which ranges are considered expired (30 minutes).
 * Expired ranges are automatically evicted.
 */
const RANGE_EXPIRY_MS = 30 * 60 * 1000;

// Map of range ID → tracked range entry
const trackedRanges = new Map<string, TrackedRangeEntry>();

// Set of range IDs that were released (evicted or expired)
// Used to provide helpful error messages when accessing released ranges
const releasedRangeIds = new Set<string>();

/**
 * Maximum size of released range ID tracking set.
 * When exceeded, oldest IDs are pruned.
 */
const MAX_RELEASED_IDS = 500;

/**
 * Global counter for unique ID generation.
 * Increments on each call to ensure uniqueness.
 */
let rangeIdCounter = 0;

/**
 * Generate a unique range ID.
 * Format: "range-{counter}"
 */
export function generateRangeId(): string {
  return `range-${rangeIdCounter++}`;
}

/**
 * Evict least-recently-used ranges if we're at capacity.
 * Also evicts expired ranges regardless of capacity.
 */
function evictIfNeeded(): void {
  const now = Date.now();

  // First, evict expired ranges
  const expiredIds: string[] = [];
  for (const [id, entry] of trackedRanges) {
    if (now - entry.lastAccessedAt > RANGE_EXPIRY_MS) {
      expiredIds.push(id);
    }
  }
  for (const id of expiredIds) {
    releaseRange(id);
  }

  // Then, if still over capacity, evict LRU entries
  while (trackedRanges.size >= MAX_TRACKED_RANGES) {
    // Find the least recently accessed entry
    let oldestId: string | null = null;
    let oldestAccessTime = Infinity;

    for (const [id, entry] of trackedRanges) {
      if (entry.lastAccessedAt < oldestAccessTime) {
        oldestAccessTime = entry.lastAccessedAt;
        oldestId = id;
      }
    }

    if (oldestId) {
      releaseRange(oldestId);
    } else {
      break;
    }
  }
}

/**
 * Release a range (untrack and remove from store).
 * Tracks the ID in releasedRangeIds for error messaging.
 */
function releaseRange(rangeId: string): void {
  const entry = trackedRanges.get(rangeId);
  if (entry) {
    try {
      entry.range.untrack();
    } catch {
      // Range may already be invalid, ignore
    }
    trackedRanges.delete(rangeId);

    // Track released ID (prune if too many)
    if (releasedRangeIds.size >= MAX_RELEASED_IDS) {
      // Remove first (oldest) entries - Set maintains insertion order
      const idsToRemove = Array.from(releasedRangeIds).slice(0, Math.floor(MAX_RELEASED_IDS / 2));
      for (const id of idsToRemove) {
        releasedRangeIds.delete(id);
      }
    }
    releasedRangeIds.add(rangeId);
  }
}

/**
 * Store a tracked range with the given ID.
 * The range should already have `.track()` called on it.
 *
 * @param rangeId - Unique identifier for the range
 * @param range - The Word.Range object (already tracked)
 * @param persistent - If true, range persists after edits (from query_range).
 *                     If false/undefined, range is removed after setSnippet.
 * @param metadata - Optional metadata for re-finding the range in new contexts.
 *
 * Automatically evicts old ranges if at capacity.
 */
export function storeTrackedRange(
  rangeId: string,
  range: Word.Range,
  persistent?: boolean,
  metadata?: RangeMetadata,
): void {
  evictIfNeeded();

  const now = Date.now();
  trackedRanges.set(rangeId, {
    range,
    createdAt: now,
    lastAccessedAt: now,
    persistent,
    metadata,
  });
}

/**
 * Get the metadata for a tracked range.
 * Used for re-finding the range in a new Word context.
 */
export function getTrackedRangeMetadata(rangeId: string): RangeMetadata | undefined {
  const entry = trackedRanges.get(rangeId);
  return entry?.metadata;
}

/**
 * Get a tracked range by ID.
 * Updates the lastAccessedAt timestamp for LRU tracking.
 */
export function getTrackedRange(rangeId: string): Word.Range | undefined {
  const entry = trackedRanges.get(rangeId);
  if (entry) {
    // Update last accessed time for LRU
    entry.lastAccessedAt = Date.now();
    return entry.range;
  }
  return undefined;
}

/**
 * Check if a range ID was previously valid but has been released.
 * This is used to provide helpful error messages.
 */
export function isRangeReleased(rangeId: string): boolean {
  return releasedRangeIds.has(rangeId);
}

/**
 * Check if a tracked range is persistent (from query_range).
 * Persistent ranges should NOT be removed after setSnippet edits.
 */
export function isRangePersistent(rangeId: string): boolean {
  const entry = trackedRanges.get(rangeId);
  return entry?.persistent ?? false;
}

/**
 * Remove and untrack a range by ID.
 * Returns true if the range was found and removed.
 */
export function removeTrackedRange(rangeId: string): boolean {
  const entry = trackedRanges.get(rangeId);
  if (entry) {
    try {
      entry.range.untrack();
    } catch {
      // Range may already be invalid, ignore
    }
    trackedRanges.delete(rangeId);
    return true;
  }
  return false;
}

/**
 * Clear all tracked ranges.
 * Returns the IDs that were cleared.
 */
export function clearAllTrackedRanges(): string[] {
  const clearedIds = Array.from(trackedRanges.keys());
  for (const entry of trackedRanges.values()) {
    try {
      entry.range.untrack();
    } catch {
      // Range may already be invalid, ignore
    }
  }
  trackedRanges.clear();
  releasedRangeIds.clear();
  return clearedIds;
}

/**
 * Get count of tracked ranges.
 */
export function getTrackedRangeCount(): number {
  return trackedRanges.size;
}

/**
 * Get statistics about the tracked range store.
 * Useful for debugging and monitoring.
 */
export function getTrackedRangeStats(): {
  count: number;
  maxCapacity: number;
  releasedCount: number;
  oldestAgeMs: number | null;
} {
  let oldestAccessTime = Infinity;
  const now = Date.now();

  for (const entry of trackedRanges.values()) {
    if (entry.lastAccessedAt < oldestAccessTime) {
      oldestAccessTime = entry.lastAccessedAt;
    }
  }

  return {
    count: trackedRanges.size,
    maxCapacity: MAX_TRACKED_RANGES,
    releasedCount: releasedRangeIds.size,
    oldestAgeMs: oldestAccessTime === Infinity ? null : now - oldestAccessTime,
  };
}
